import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ProductService, Product } from '../api/api_pb';
import { useClient } from '../connect_api';
import RelativeTime from '../components/RelativeTime';
import { toast } from 'react-toastify';
import CurrencyFormatter from '../utils/currencyFormatter';
import CreateProductModal from '../components/CreateProductModal';
import { useGlobalContext } from '../GlobalContext';
import { 
    ShoppingCart, 
    Plus,
    Edit2,
    Clock,
    DollarSign,
    Image as ImageIcon,
    Calendar,
    Timer,
    CheckCircle,
    XCircle,
    Layers,
    Search
} from 'lucide-react';

const Products: React.FC = () => {
    const productClient = useClient(ProductService);
    const [products, setProducts] = useState<Product[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const { updateProducts } = useGlobalContext();

    const formatDuration = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const formattedSeconds = remainingSeconds.toString().padStart(2, '0');

        const formattedTime = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
        return formattedTime === '00:00:00' ? 'No limits' : formattedTime;
    };

    const navigate = useNavigate();

    const handleStockClick = (product: Product) => {
        updateProducts(product);
        navigate(`/stocks/${product.uuid}`, { state: { product } });
    };

    const handleEditClick = (product: Product) => {
        updateProducts(product);
        navigate(`/edit-product/${product.uuid}`, { state: { product } });
    };

    const fetchProducts = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await productClient.listProducts({});
            const fetchedProducts = response.products;

            if (fetchedProducts.length === 0) {
                setError('No products available');
            } else {
                setProducts(fetchedProducts);
                setError(null);
            }
        } catch (error) {
            console.error('Failed to fetch products:', error);
            setError('Failed to fetch products. Please try again later.');
            toast.dismiss();
            toast.error('Failed to fetch products');
        } finally {
            setIsLoading(false);
        }
    }, [productClient]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Filter products based on search term
    const filteredProducts = products.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.uuid.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const ProductCard = ({ product }: { product: Product }) => (
        <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group">
            {/* Product Image */}
            <div className="relative h-48 bg-gray-50 overflow-hidden">
                {product.imageUrl ? (
                    <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-300"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-gray-300" />
                    </div>
                )}
                {/* Sale Status Badge */}
                <div className="absolute top-3 right-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        product.forSale 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                    }`}>
                        {product.forSale ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {product.forSale ? 'For Sale' : 'Not for Sale'}
                    </span>
                </div>
            </div>

            {/* Card Content */}
            <div className="p-6">
                {/* Header */}
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{product.name}</h3>
                    <p className="text-xs text-gray-500 font-mono truncate">{product.uuid}</p>
                </div>

                {/* Info Grid */}
                <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Price</span>
                            <p className="text-lg font-bold text-gray-900">
                                <CurrencyFormatter priceInCents={product.priceInCents} />
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start gap-2">
                        <Timer className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Lease Duration Limit</span>
                            <p className="text-gray-900 font-medium">{formatDuration(product.leaseDurationLimit)}</p>
                        </div>
                    </div>

                    {product.createdAt && (
                        <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-sm text-gray-600">Created</span>
                                <p className="text-sm text-gray-900">
                                    <RelativeTime timestamp={product.createdAt} />
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                    <button
                        onClick={() => handleStockClick(product)}
                        className="flex-1 btn-primary flex items-center justify-center gap-2"
                    >
                        <Layers className="w-4 h-4" />
                        Stocks
                    </button>
                    <button
                        onClick={() => handleEditClick(product)}
                        className="flex-1 btn-secondary flex items-center justify-center gap-2"
                    >
                        <Edit2 className="w-4 h-4" />
                        Edit
                    </button>
                </div>
            </div>
        </div>
    );

    const ProductListItem = ({ product }: { product: Product }) => (
        <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-4 border border-gray-100">
            <div className="flex items-center gap-4">
                {/* Product Image */}
                <div className="w-20 h-20 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                    {product.imageUrl ? (
                        <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="w-full h-full object-contain p-2"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-gray-300" />
                        </div>
                    )}
                </div>
                
                {/* Main Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            product.forSale 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                        }`}>
                            {product.forSale ? 'For Sale' : 'Not for Sale'}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 font-mono mb-2">{product.uuid}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            <span className="font-bold text-gray-900"><CurrencyFormatter priceInCents={product.priceInCents} /></span>
                        </span>
                        <span className="flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            {formatDuration(product.leaseDurationLimit)}
                        </span>
                        {product.createdAt && (
                            <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <RelativeTime timestamp={product.createdAt} />
                            </span>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                    <button
                        onClick={() => handleStockClick(product)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="View Stocks"
                    >
                        <Layers className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleEditClick(product)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit Product"
                    >
                        <Edit2 className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-6 animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Products</h1>
                        <p className="text-gray-600">Manage your vending machine products</p>
                    </div>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="btn-primary flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Create Product
                    </button>
                </div>
            </div>

            {/* Controls Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-modern pl-10 pr-4 py-2 w-full"
                        />
                    </div>
                    
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`px-3 py-1.5 rounded-md transition-colors ${
                                viewMode === 'grid' 
                                    ? 'bg-white text-gray-900 shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Grid
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-1.5 rounded-md transition-colors ${
                                viewMode === 'list' 
                                    ? 'bg-white text-gray-900 shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            List
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Total Products</p>
                        <p className="text-3xl font-bold text-gray-900">{products.length}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {products.filter(p => p.forSale).length} for sale, {products.filter(p => !p.forSale).length} not for sale
                        </p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                        <ShoppingCart className="w-8 h-8 text-white" />
                    </div>
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading products...</p>
                    </div>
                </div>
            ) : error && products.length === 0 ? (
                <div className="text-center py-12">
                    <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No Products Found</h2>
                    <p className="text-gray-600 mb-4">Get started by creating your first product.</p>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="btn-primary inline-flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Create Product
                    </button>
                </div>
            ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                    <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No Results Found</h2>
                    <p className="text-gray-600">No products match your search criteria.</p>
                </div>
            ) : (
                <>
                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredProducts.map((product) => (
                                <ProductCard key={product.uuid} product={product} />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredProducts.map((product) => (
                                <ProductListItem key={product.uuid} product={product} />
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Create Product Modal */}
            <CreateProductModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onProductCreated={fetchProducts}
            />
        </div>
    );
};

export default Products;